import { Controller } from "@hotwired/stimulus"
import { TRIPS_FILTER_EVENT, type TripsFilterDetail } from "../components/TripsWorldMap/events"

/**
 * Content browse controller - search + country, style and length chips for the /travel and /compare hubs
 *
 * Usage:
 *   data-controller="content-browse" on the section
 *   data-content-browse-noun-value="comparison" names the items in the result line ("guide" by default)
 *   data-content-browse-style-param-value="category" keeps the style chips in ?category= (the blog) instead of ?style=
 *   data-content-browse-target="search" on the search input (optional: /stay has none)
 *   data-content-browse-target="clearBtn" on the clear-search button (with the search input)
 *   data-content-browse-target="countryBtn" on country chips (data-country="all" | country slug, data-name; on
 *     /travel also data-continent)
 *   data-content-browse-target="continentBtn" on continent chips (data-continent="all" | europe..., data-name), optional:
 *     a continent narrows the cards (data-continent) and shows its own country chips - with continent chips the
 *     country chips stay hidden until a continent (or ?country=) is picked
 *   data-content-browse-target="styleBtn" on trip style chips (data-style="all" | sightseeing | beach..., data-name), only when there are 2+ styles
 *   data-content-browse-target="daysBtn" on length chips (data-days="all" | 3 | 5), only when there are 2+ lengths
 *   data-content-browse-target="difficultyBtn" on difficulty chips (data-difficulty="all" | easy | hard..., data-name), /adventures
 *   data-content-browse-target="styleLink" on links shown while their style is picked (data-style; /adventures: the activity hub)
 *   data-content-browse-target="countryLink" on "More about X" links (data-country)
 *   data-content-browse-target="featured" on the large newest-guide card (optional)
 *   Required on every page: "count", "empty", "emptyTitle", "grid" and the cards; search, clearBtn and the chip rows are optional
 *   data-content-browse-target="grid" on the grid, "card" on each card wrapper
 *     (data-slug, data-days, data-styles="sightseeing beach", data-country="greece" or "greece egypt" for a
 *     comparison of two countries, data-featured="true|false", data-search="city country continent")
 *   data-content-browse-target="map" on the world map root (optional) - gets data-slugs, and
 *     every change is also sent as a TRIPS_FILTER_EVENT for the mounted map
 *   data-content-browse-target="count" / "empty" / "emptyTitle"
 *
 * Each chip's count (span[data-chip-count]) shows how many items it would give with the other filters as they are.
 * State is kept in ?country=, ?continent=, ?style=, ?days=, ?difficulty= and ?q= so a filtered list can be shared.
 */
export default class extends Controller<HTMLElement> {
  static values = { noun: { type: String, default: "guide" }, styleParam: { type: String, default: "style" } }
  static targets = ["search", "clearBtn", "countryBtn", "countryLink", "featured", "grid", "card", "count", "empty", "emptyTitle", "map", "daysBtn", "styleBtn", "continentBtn", "difficultyBtn", "styleLink"]

  declare readonly searchTarget: HTMLInputElement
  declare readonly hasSearchTarget: boolean
  declare readonly clearBtnTarget: HTMLElement
  declare readonly hasClearBtnTarget: boolean
  declare readonly countryBtnTargets: HTMLButtonElement[]
  declare readonly countryLinkTargets: HTMLElement[]
  declare readonly featuredTarget: HTMLElement
  declare readonly hasFeaturedTarget: boolean
  declare readonly nounValue: string
  declare readonly styleParamValue: string
  declare readonly gridTarget: HTMLElement
  declare readonly cardTargets: HTMLElement[]
  declare readonly countTarget: HTMLElement
  declare readonly emptyTarget: HTMLElement
  declare readonly emptyTitleTarget: HTMLElement
  declare readonly mapTarget: HTMLElement
  declare readonly hasMapTarget: boolean
  declare readonly daysBtnTargets: HTMLButtonElement[]
  declare readonly styleBtnTargets: HTMLButtonElement[]
  declare readonly continentBtnTargets: HTMLButtonElement[]
  declare readonly difficultyBtnTargets: HTMLButtonElement[]
  declare readonly styleLinkTargets: HTMLElement[]

  private activeCountry = "all"
  private activeContinent = "all"
  private activeDays = "all"
  private activeStyle = "all"
  private activeDifficulty = "all"
  private query = ""
  private debounceTimer: ReturnType<typeof setTimeout> | null = null
  private initialCount = ""

  connect() {
    this.initialCount = this.countTarget.textContent?.trim() ?? ""

    const params = new URLSearchParams(window.location.search)
    const country = params.get("country")
    const query = params.get("q")
    const days = params.get("days")
    const style = params.get(this.styleParamValue)
    const continent = params.get("continent")
    const difficulty = params.get("difficulty")

    if (country && this.countryBtnTargets.some(btn => btn.dataset.country === country)) {
      this.activeCountry = country
    }
    if (continent && this.continentBtnTargets.some(btn => btn.dataset.continent === continent)) {
      this.activeContinent = continent
    }
    this.dropCountryOutsideContinent()
    // A country from a link (?country=greece, the country page's "Plan a trip") lights its continent, so
    // its neighbours and "All" show next to it
    if (this.activeCountry !== "all" && this.activeContinent === "all" && this.continentBtnTargets.length > 0) {
      this.activeContinent = this.countryContinent(this.activeCountry) || "all"
    }
    if (days && this.daysBtnTargets.some(btn => btn.dataset.days === days)) {
      this.activeDays = days
    }
    if (style && this.styleBtnTargets.some(btn => btn.dataset.style === style)) {
      this.activeStyle = style
    }
    if (difficulty && this.difficultyBtnTargets.some(btn => btn.dataset.difficulty === difficulty)) {
      this.activeDifficulty = difficulty
    }
    if (query && this.hasSearchTarget) {
      this.searchTarget.value = query
      this.query = query.trim()
    }

    this.apply()
    // A kind of trip, a length or a difficulty from the URL opens the "Filters" panel, so the picked chip shows
    if (this.panelPicks() > 0) this.filtersPanel?.setAttribute("open", "")
  }

  disconnect() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  search() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)

    this.debounceTimer = setTimeout(() => {
      this.query = this.searchTarget.value.trim()
      this.apply()
      this.updateUrl()
    }, 120)
  }

  clearSearch() {
    this.searchTarget.value = ""
    this.query = ""
    this.apply()
    this.updateUrl()
    this.searchTarget.focus()
  }

  filterCountry(event: Event) {
    const button = event.currentTarget as HTMLButtonElement
    const country = button.dataset.country || "all"

    // Tapping the active chip again goes back to everything
    this.activeCountry = country === this.activeCountry ? "all" : country
    this.apply()
    this.updateUrl()
  }

  filterContinent(event: Event) {
    const continent = (event.currentTarget as HTMLButtonElement).dataset.continent || "all"

    this.activeContinent = continent === this.activeContinent ? "all" : continent
    this.dropCountryOutsideContinent()
    this.apply()
    this.updateUrl()
  }

  // A country from another continent would leave nothing to show (a click, or a hand-edited link)
  private dropCountryOutsideContinent() {
    if (this.activeContinent === "all") return

    if (this.countryContinent(this.activeCountry) !== this.activeContinent) this.activeCountry = "all"
  }

  private countryContinent(country: string): string | undefined {
    return this.countryBtnTargets.find(btn => btn.dataset.country === country)?.dataset.continent
  }

  filterDays(event: Event) {
    const days = (event.currentTarget as HTMLButtonElement).dataset.days || "all"

    this.activeDays = days === this.activeDays ? "all" : days
    this.apply()
    this.updateUrl()
  }

  filterDifficulty(event: Event) {
    const difficulty = (event.currentTarget as HTMLButtonElement).dataset.difficulty || "all"

    this.activeDifficulty = difficulty === this.activeDifficulty ? "all" : difficulty
    this.apply()
    this.updateUrl()
  }

  filterStyle(event: Event) {
    const style = (event.currentTarget as HTMLButtonElement).dataset.style || "all"

    this.activeStyle = style === this.activeStyle ? "all" : style
    this.apply()
    this.updateUrl()
  }

  reset() {
    if (this.hasSearchTarget) this.searchTarget.value = ""
    this.query = ""
    this.activeCountry = "all"
    this.activeContinent = "all"
    this.activeDays = "all"
    this.activeStyle = "all"
    this.activeDifficulty = "all"
    this.apply()
    this.updateUrl()
  }

  private apply() {
    const filtering = this.activeCountry !== "all" || this.activeContinent !== "all" || this.activeDays !== "all" || this.activeStyle !== "all" || this.activeDifficulty !== "all" || this.query !== ""
    const needle = normalize(this.query)

    this.daysBtnTargets.forEach(btn => this.styleChip(btn, (btn.dataset.days || "all") === this.activeDays))
    this.styleBtnTargets.forEach(btn => this.styleChip(btn, (btn.dataset.style || "all") === this.activeStyle))
    this.difficultyBtnTargets.forEach(btn => this.styleChip(btn, (btn.dataset.difficulty || "all") === this.activeDifficulty))

    this.countryBtnTargets.forEach(btn => this.styleChip(btn, (btn.dataset.country || "all") === this.activeCountry))
    this.continentBtnTargets.forEach(btn => this.styleChip(btn, (btn.dataset.continent || "all") === this.activeContinent))
    this.showCountryChips()
    this.labelFilters()

    this.revealActiveChip()

    const active: Facets = { country: this.activeCountry, continent: this.activeContinent, days: this.activeDays, style: this.activeStyle, difficulty: this.activeDifficulty }
    this.updateCounts(active, needle)

    let visible = 0
    const slugs: string[] = []
    this.cardTargets.forEach(card => {
      const matches = cardMatches(card, active, needle)
      if (matches) {
        visible++
        if (card.dataset.slug) slugs.push(card.dataset.slug)
      }

      // Unfiltered, the newest guide is shown as the large featured card instead
      const show = matches && (filtering || card.dataset.featured !== "true")
      card.classList.toggle("hidden", !show)
    })

    if (this.hasFeaturedTarget) this.featuredTarget.classList.toggle("hidden", filtering)
    this.gridTarget.classList.toggle("hidden", visible === 0)
    this.emptyTarget.classList.toggle("hidden", visible > 0)
    if (this.hasClearBtnTarget) this.clearBtnTarget.classList.toggle("hidden", this.query === "")

    this.countryLinkTargets.forEach(link => {
      link.classList.toggle("hidden", visible === 0 || link.dataset.country !== this.activeCountry)
    })
    this.styleLinkTargets.forEach(link => {
      link.classList.toggle("hidden", visible === 0 || link.dataset.style !== this.activeStyle)
    })

    // With no results the empty state says it all
    this.countTarget.classList.toggle("hidden", visible === 0)
    this.countTarget.textContent = filtering ? this.resultLabel(visible) : this.initialCount
    // "No beach guide for “berlin” yet" - name the kind of trip, it is often why nothing matched
    const style = this.styleBtnTargets.find(btn => btn.dataset.style === this.activeStyle)?.dataset.name
    const difficulty = this.difficultyBtnTargets.find(btn => btn.dataset.difficulty === this.activeDifficulty)?.dataset.name
    const guide = [difficulty, style, this.nounValue].filter(Boolean).join(" ")
    this.emptyTitleTarget.textContent = this.query
      ? `No ${guide} for “${this.query}” yet`
      : `No ${guide} for that yet`

    this.syncMap(filtering ? slugs : null)
  }

  // A chip's count = items matching every other active filter plus that chip ("Beach" while Greece is picked)
  private updateCounts(active: Facets, needle: string) {
    const groups: [HTMLButtonElement[], keyof Facets, string][] = [
      [this.countryBtnTargets, "country", "country"],
      [this.continentBtnTargets, "continent", "continent"],
      [this.daysBtnTargets, "days", "days"],
      [this.styleBtnTargets, "style", "style"],
      [this.difficultyBtnTargets, "difficulty", "difficulty"],
    ]

    groups.forEach(([buttons, facet, attribute]) => {
      buttons.forEach(btn => {
        const value = btn.dataset[attribute] || "all"
        const facets = { ...active, [facet]: value }
        // Picking another continent drops the country, so count it the way the click would show it
        if (facet === "continent" && value !== "all" && active.country !== "all" && this.countryContinent(active.country) !== value) facets.country = "all"
        const count = this.cardTargets.filter(card => cardMatches(card, facets, needle)).length
        const label = btn.querySelector<HTMLElement>("[data-chip-count]")
        if (label) label.textContent = String(count)
        btn.classList.toggle("opacity-50", count === 0 && value !== active[facet])
      })
    })
  }

  private get filtersPanel(): HTMLDetailsElement | null {
    return this.element.querySelector<HTMLDetailsElement>("details.travel-filters")
  }

  // "Filters · 1" while a chip inside the panel is picked
  private labelFilters() {
    const summary = this.filtersPanel?.querySelector("summary")
    if (!summary) return
    const picked = this.panelPicks()
    summary.textContent = picked > 0 ? `Filters · ${picked}` : "Filters"
  }

  // Picks made inside the "Filters" panel: the style chips sit there on /travel, in the open on /adventures and /stay
  private panelPicks(): number {
    const panel = this.filtersPanel
    if (!panel) return 0
    const inPanel = (buttons: HTMLButtonElement[]) => buttons.length > 0 && panel.contains(buttons[0])
    return [
      inPanel(this.styleBtnTargets) && this.activeStyle !== "all",
      inPanel(this.daysBtnTargets) && this.activeDays !== "all",
      inPanel(this.difficultyBtnTargets) && this.activeDifficulty !== "all",
    ].filter(Boolean).length
  }

  // Without continent chips (blog, compare) every country chip shows. With them a continent shows its own
  // countries, and the picked country always shows (also from ?country= in a link)
  private showCountryChips() {
    if (this.continentBtnTargets.length === 0) return

    const picking = this.activeContinent !== "all" || this.activeCountry !== "all"
    this.countryBtnTargets.forEach(btn => {
      const country = btn.dataset.country || "all"
      const show = country === this.activeCountry ||
        (this.activeContinent !== "all" && (country === "all" || btn.dataset.continent === this.activeContinent))
      btn.classList.toggle("hidden", !(picking && show))
    })
  }

  // The map mounts lazily: the attribute covers a filter set before it exists, the event every change after
  private syncMap(slugs: string[] | null) {
    if (!this.hasMapTarget) return

    if (slugs === null) delete this.mapTarget.dataset.slugs
    else this.mapTarget.dataset.slugs = JSON.stringify(slugs)

    window.dispatchEvent(new CustomEvent<TripsFilterDetail>(TRIPS_FILTER_EVENT, { detail: { slugs } }))
  }

  // On phones a chip row scrolls sideways - keep the selected chip in view (e.g. after ?country=italy or ?category=geography)
  private revealActiveChip() {
    const chips = [
      this.countryBtnTargets.find(btn => (btn.dataset.country || "all") === this.activeCountry),
      this.styleBtnTargets.find(btn => (btn.dataset.style || "all") === this.activeStyle),
      this.continentBtnTargets.find(btn => (btn.dataset.continent || "all") === this.activeContinent),
    ]

    chips.forEach(chip => {
      const scroller = chip?.closest<HTMLElement>(".overflow-x-auto")
      if (!chip || !scroller || scroller.scrollWidth <= scroller.clientWidth) return

      const offset = chip.getBoundingClientRect().left - scroller.getBoundingClientRect().left
      scroller.scrollLeft += offset - (scroller.clientWidth - chip.offsetWidth) / 2
    })
  }

  // Selected chips are solid navy, the others ghost buttons with a hairline
  private styleChip(btn: HTMLButtonElement, active: boolean) {
    btn.setAttribute("aria-pressed", String(active))
    btn.classList.toggle("btn-primary", active)
    btn.classList.toggle("btn-ghost", !active)
    btn.classList.toggle("border", !active)
    btn.classList.toggle("border-base-300", !active)
    btn.classList.toggle("bg-base-100", !active)
  }

  private resultLabel(visible: number): string {
    const style = this.styleBtnTargets.find(btn => btn.dataset.style === this.activeStyle)?.dataset.name
    const length = this.activeDays === "all" ? "" : ` for ${this.activeDays} days`
    const difficulty = this.difficultyBtnTargets.find(btn => btn.dataset.difficulty === this.activeDifficulty)?.dataset.name
    const guides = `${visible} ${difficulty ? `${difficulty} ` : ""}${style ? `${style} ` : ""}${visible === 1 ? this.nounValue : `${this.nounValue}s`}${length}`
    const country = this.countryBtnTargets.find(btn => btn.dataset.country === this.activeCountry)?.dataset.name ??
      this.continentBtnTargets.find(btn => btn.dataset.continent === this.activeContinent)?.dataset.name

    if (country && this.query) return `${guides} in ${country} for “${this.query}”`
    if (country) return `${guides} in ${country}`
    if (this.query) return `${guides} for “${this.query}”`
    return guides
  }

  private updateUrl() {
    const url = new URL(window.location.href)

    if (this.activeCountry === "all") url.searchParams.delete("country")
    else url.searchParams.set("country", this.activeCountry)

    if (this.activeContinent === "all") url.searchParams.delete("continent")
    else url.searchParams.set("continent", this.activeContinent)

    if (this.activeStyle === "all") url.searchParams.delete(this.styleParamValue)
    else url.searchParams.set(this.styleParamValue, this.activeStyle)

    if (this.activeDays === "all") url.searchParams.delete("days")
    else url.searchParams.set("days", this.activeDays)

    if (this.activeDifficulty === "all") url.searchParams.delete("difficulty")
    else url.searchParams.set("difficulty", this.activeDifficulty)

    if (this.query === "") url.searchParams.delete("q")
    else url.searchParams.set("q", this.query)

    history.replaceState(history.state, "", url)
  }
}

interface Facets {
  country: string
  continent: string
  days: string
  style: string
  difficulty: string
}

function cardMatches(card: HTMLElement, facets: Facets, needle: string): boolean {
  return (facets.country === "all" || (card.dataset.country || "").split(" ").includes(facets.country)) &&
    (facets.continent === "all" || card.dataset.continent === facets.continent) &&
    (facets.days === "all" || card.dataset.days === facets.days) &&
    (facets.style === "all" || (card.dataset.styles || "").split(" ").includes(facets.style)) &&
    (facets.difficulty === "all" || card.dataset.difficulty === facets.difficulty) &&
    (needle === "" || normalize(card.dataset.search || "").includes(needle))
}

// Case and accent insensitive, so "krakow" finds Kraków and "izmir" finds İzmir
function normalize(text: string): string {
  return text.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase()
}
